import {to_getFriendlyName} from "@Common/logic/template-options/template-options-get-friendly-name";
import {cs}                 from "@Common/react/chain-services";
import {consumeContext}     from "@Common/react/context";
import {formatMoney1}       from "@Common/ui-components/language/format-money";
import {plural}             from "@Common/ui-components/language/plural";
import {cnv}                from "@Common/utils/arr-path";
import {cascadeCount}       from "@Common/utils/cascade";

import {Distribution}            from "./distribution/distribution";
import {loadTemplateOptionsList} from "./load-template-options-list";
import {Payment}                 from "./payment/payment";
import {Personalization}         from "./personalization/personalization";
import {QrCode}                  from "./personalization/qr-code/qr-code";
import {Editions}                from "./shared-components/editions/editions";
import {EnrollmentSummary}       from "./shared-components/enrollment-summary/enrollment-summary";

export const EnrollmentSteps = ({initEnrollment, program, agentId, branchId, getStyleTemplate, next}) => cs(
    consumeContext("session"),
    ({session}, next) => session.data.agent && next(),

    // Shared Components
    ["editions", (_, next) => Editions({
        initEnrollment,
        program,
        getStyleTemplate,
        next,
    })],

    ["templateOptionsList", ({editions}, next) => loadTemplateOptionsList({
        allTemplateIds: editions.allTemplateIds,
        selectedTemplateIds: editions.selectedTemplateIds,
        next,
    })],


    ["qrCode", ({templateOptionsList}, next) => QrCode({
        initEnrollment,
        templateOptions: templateOptionsList.selected?.[0],
        program,
        next,
    })],
    ["personalization", ({editions, qrCode}, next) => Personalization({
        initEnrollment, program,
        editionList: editions.editionList,
        agentId,
        qrCode: qrCode.forPersonalization(),
        next,
    })],

    ["distribution", ({templateOptionsList}, next) => Distribution({
        initEnrollment, program,
        templateOptions: templateOptionsList.selected?.[0],
        agentId, branchId,
        next,
    })],

    ["estimatedTotal", ({distribution, qrCode}, next) => next(
        distribution.estimatedTotal +
        (qrCode.getEstimatedTotal?.(distribution.totalQuantity) ?? 0)
    )],
    ["renderSummary", ({editions, templateOptionsList, distribution, qrCode, estimatedTotal}, next) => next(
        ({className=undefined}={}) => EnrollmentSummary({
            subtitle: `Each Edition will be mailed/shipped within 5 business days after the Final Day to Edit period. Your enrollment contains ${cascadeCount(editions.seriesSummary, "[*].editions[*]")} editions`,
            sections: [
                {
                    title: "Enrolled Editions",
                    // tooltip: ,
                    lines: editions.seriesSummary.map((series) => ({
                        type: "expandable-line",
                        name: `Series: ${series.name} (${series.editions.length} ${plural("edition", series.editions.length)})`,
                        subLines: series.editions.map((edition) => {
                            const templateOptions = templateOptionsList.all.find((to) => to?.sku?.front == edition.templateId);
                            return ({
                                label: edition.name,
                                subLabel: to_getFriendlyName(templateOptions) ?? templateOptions?.product.name,
                                value: distribution.getPricePerPiece(templateOptions),
                            });
                        }),
                    })),
                },
                {
                    title: "Distribution",
                    lines: [
                        ...distribution.getSummaryLines({templateOptionsList: templateOptionsList.selected?.filter(v=>v)}),
                        ...qrCode.getSummaryLines?.(distribution.totalQuantity)??[],
                    ].filter(v=>v),
                },
                {
                    title: "Estimated total for the next mailer:",
                    disclaimer: "Mailed order pricing includes postage, shipped orders do not include shipping cost",
                    lines: [
                        {
                            name: `Series: ${editions.seriesSummary?.[0]?.name} - ${editions.seriesSummary?.[0]?.editions?.[0]?.name}`,
                            value: formatMoney1(estimatedTotal),
                            valueDisplay: {
                                color: "green",
                                fontWeight: "bold",
                            },
                        },
                    ],
                },
            ],
            className,
        })
    )],

    ["payment", ({editions, estimatedTotal}, next) => Payment({
        initEnrollment, program,
        upcomingEdition: editions.upcomingEdition,
        agentId,
        estimatedTotal,
        next,
    })],
    ["steps", ({editions, personalization, distribution, payment, renderSummary, templateOptionsList}) => next([
        {
            label: "Select Editions",
            arLabel: "Enrolled Schedule",
            icon: "calendar",
            ...editions.selectEditions,
            render: () => editions.selectEditions.render({templateOptionsList}),
        },
        {
            label: "Personalization",
            icon: "id-badge",
            ...personalization,
        },
        {
            label: "Distribution",
            icon: "truck",
            ...distribution,
            enrollmentEditor: cnv(distribution.enrollmentEditor, ["loading"], (_) => _ || templateOptionsList.loading),
            render: ({showErrors}) => distribution.render({showErrors, renderSummary}),
        },
        {
            label: "Payment",
            icon: "money",
            ...payment,
            render: ({showErrors}) => payment.render({showErrors, renderSummary}),
        },
    ])],
    ({steps}) => next(steps),
);

